import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useGuardedNavigate } from "../../hooks/useGuardedNavigate";
import {
  confirmDiscardFormEdits,
  markFormSaved,
  useFormEditingGuard,
} from "../../hooks/useFormEditingGuard";
import {
  Layout,
  theme,
  Tabs,
  Row,
  Col,
  Card,
  Button,
  message,
  Input,
  Select,
  Modal,
  Form,
  Space,
  Switch,
  Upload,
  Divider,
} from "antd";
import {
  GoldOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { TabsProps, UploadProps } from "antd";
import { SelectProps } from "antd/lib/select";
import axios from "axios";
import LoadingOverlay from "react-loading-overlay-ts";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import SecretText from "../../components/SecretText";
import { ProductTable } from "../../components/Table";
import AddonTable from "../../components/Table/AddonTable";
import GroupCommunicationTab from "../../components/GroupCommunication/GroupCommunicationTab";
import CommunicationSetupAlert from "../../components/GroupCommunication/CommunicationSetupAlert";
import {
  getCommunicationSetupIssues,
  COMMUNICATION_SECTIONS,
} from "../../components/GroupCommunication/communicationSetupChecks";
import {
  getGroupTabState,
  setGroupTabState,
} from "../../utils/groupTabStorage";

import {
  getGroupApi,
  editGroupApi,
  sendGroupSmsApi,
} from "../../services/Group/GroupApis";
import {
  getDocumentsApi,
  removeDocumentApi,
} from "../../services/Document/DocumentApis";
import {
  addProductApi,
  editProductApi,
  removeProductApi,
} from "../../services/Product/ProductApis";
import { addAddonApi, editAddonApi } from "../../services/Addon/AddonApis";

const { Content } = Layout;

interface IGroupProps {
  group: string;
}

type TextAreaRefs = {
  [key: string]: React.RefObject<any>;
};

const bank: SelectProps["options"] = [
  {
    label: "ABSA",
    value: "ABSA",
  },
  {
    label: "Access",
    value: "Access",
  },
  {
    label: "African Bank",
    value: "African Bank",
  },
  {
    label: "Bidvest Bank",
    value: "Bidvest Bank",
  },
  {
    label: "Capitec",
    value: "Capitec",
  },
  {
    label: "FinBond",
    value: "FinBond",
  },
  {
    label: "FNB",
    value: "FNB",
  },
  {
    label: "Nedbank",
    value: "Nedbank",
  },
  {
    label: "Standard Bank",
    value: "Standard Bank",
  },
  {
    label: "TymeBank",
    value: "TymeBank",
  },
];

const messageType: SelectProps["options"] = [
  {
    label: "Active Members",
    value: "Active",
  },
  {
    label: "Inactive Members",
    value: "Inactive",
  },
  {
    label: "All Members",
    value: "All",
  },
];

const GroupManagementPage: React.FC<IGroupProps> = () => {
  const navigate = useGuardedNavigate();
  const [form] = Form.useForm(); // This will create a form instance
  const { groupId }: any = useParams();
  const [formDirty, setFormDirty] = useState(false);

  const [group, setGroup] = useState<any>({});
  const [products, setProducts] = useState<any>([]);
  const [addons, setAddons] = useState<any>([]);
  const [documents, setDocuments] = useState<any>([]);
  const [communicationDocuments, setCommunicationDocuments] = useState<any>([]);
  const [isActive, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string>("");
  const [key, setKey] = useState<string>("");
  const [formData, setFormData] = useState<any>();
  const [formDisabled, setFormDisabled] = useState<any>({
    docs: true,
    nutun: true,
    sms: true,
    basic: true,
  });
  const { markSaved } = useFormEditingGuard({
    id: `group-management-${groupId}`,
    formDisabled,
    formDirty,
  });
  const [customVariable, setCustomVariable] = useState("PolicyHolder");
  const [selectedTextArea, setSelectedTextArea] = useState<string>("");
  const [imageUrl, setImageUrl] = useState("");
  const [activeGroupTab, setActiveGroupTab] = useState(() => {
    const stored = getGroupTabState(groupId, false);
    return stored?.activeGroupTab || "group-info";
  });
  const [communicationSubTab, setCommunicationSubTab] = useState(() => {
    const stored = getGroupTabState(groupId, false);
    return (
      stored?.communicationSubTab || COMMUNICATION_SECTIONS.documentTemplates
    );
  });

  const textAreaRefs: TextAreaRefs = {
    NewPolicySms: useRef<any>(null),
    DocumentSms: useRef<any>(null),
    NonPaymentSms: useRef<any>(null),
    SmsLapse: useRef<any>(null),
  };

  const document: SelectProps["options"] = documents.map((doc: any) => {
    return {
      value: doc.id,
      label: doc.Name,
      key: doc.id,
    };
  });

  const emailTemplateOptions: SelectProps["options"] = communicationDocuments.map(
    (doc: any) => ({
      value: doc.id,
      label: doc.Name,
      key: doc.id,
    })
  );

  const props: UploadProps = {
    name: "file",
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const base64 = await convertFileToBase64(file);
        const accessToken = localStorage.getItem("AccessToken");
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/document/image`,
          {
            base64: base64,
          },
          {
            headers: {
              AccessToken: accessToken ? accessToken : "",
            },
          }
        );
        if (onSuccess) {
          setImageUrl(response.data.data);
          onSuccess(response.data);
        }
      } catch (error) {
        if (onError) {
          onError(new Error("Upload failed"));
        }
      }
    },
    onChange(info) {
      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
      setFormData({ name: info.file.name });
    },
  };

  const onChange = (name: string, checked: boolean) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [name]: checked,
    }));
  };

  const onChangeDocument = async (e: any) => {
    const { data } = await getDocumentsApi(group.id);

    const docType = data.filter((doc: any) => {
      return doc.id === e;
    });

    {
      form.setFieldsValue({
        DocumentType: docType[0].Type,
      });
    }
  };

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleAddProduct = async (data: any) => {
    setActive(true);

    return await addProductApi(data)
      .then((res) => {
        setProducts([...products, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleAddAddon = async (data: any) => {
    setActive(true);

    return await addAddonApi(data)
      .then((res) => {
        setProducts([...products, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleSendCustomSms = async (id: any, data: any) => {
    const hasCredentials = Boolean(
      (formData?.SmsApiKey ?? group?.SmsApiKey) &&
        (formData?.SmsApiSecret ?? group?.SmsApiSecret)
    );
    if (!hasCredentials) {
      message.error(
        "SMS credentials are empty. Fill them in under SMS Credentials first."
      );
      return;
    }

    setActive(true);

    return await sendGroupSmsApi(id, data)
      .then((res) => {
        setActive(false);
        message.success("Successfully sent SMS's");
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
        message.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to send SMS. Check credentials and try again."
        );
      });
  };

  const handleAddVariable = () => {
    if (selectedTextArea === "") {
      message.error("Please select a TextArea first.");
      return;
    }

    const textAreaRef = textAreaRefs[selectedTextArea];
    const variableId = `{{${customVariable}}}`;

    if (textAreaRef?.current) {
      const textArea = textAreaRef.current.resizableTextArea.textArea;
      const currentValue = textArea.value;
      const selectionStart = textArea.selectionStart;
      const selectionEnd = textArea.selectionEnd;

      // Insert the variable into the current value
      const newValue =
        currentValue.substring(0, selectionStart) +
        variableId +
        currentValue.substring(selectionEnd);

      // Update the value of the TextArea through the form instance
      form.setFieldsValue({ [selectedTextArea]: newValue });
    }
  };

  const handleAddDetails = async (id: any, data: any, num: number) => {
    setActive(true);
    setLoading(true);

    return await editGroupApi(id, { ...data, CompanyLogo: imageUrl })
      .then((res) => {
        if (num === 1) {
          setFormDisabled({
            docs: !formDisabled.docs,
            nutun: formDisabled.nutun,
            sms: formDisabled.sms,
            basic: formDisabled.basic,
          });
        } else if (num === 2) {
          setFormDisabled({
            docs: formDisabled.docs,
            nutun: !formDisabled.nutun,
            sms: formDisabled.sms,
            basic: formDisabled.basic,
          });
        } else if (num === 3) {
          setFormDisabled({
            docs: formDisabled.docs,
            nutun: formDisabled.nutun,
            sms: !formDisabled.sms,
            basic: formDisabled.basic,
          });
        } else {
          setFormDisabled({
            docs: formDisabled.docs,
            nutun: formDisabled.nutun,
            sms: formDisabled.sms,
            basic: !formDisabled.basic,
          });
        }

        setActive(false);
        setLoading(false);
        message.success(res.message);
        markFormSaved(markSaved, setFormDirty);
        return res;
      })
      .catch((err) => {
        setActive(false);
        setLoading(false);
        message.error(err.message);
        console.log(err);
      });
  };

  const handleUpdateProduct = async (
    id: string,
    planId: any,
    categoryId: any,
    data: any
  ) => {
    setActive(true);

    return await editProductApi(id, planId, categoryId, data)
      .then((res) => {
        const updatedProducts: any = products.map((product: any) => {
          if (Number(id) === Number(product.id)) {
            return { ...product, ...res.data };
          }

          return product;
        });
        setActive(false);
        setProducts(updatedProducts);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleUpdateAddon = async (id: string, data: any) => {
    setActive(true);

    return await editAddonApi(id, data)
      .then((res) => {
        const updatedAddons: any = products.map((addon: any) => {
          if (id === addon.id) {
            return { ...addon, ...res.data };
          }

          return addon;
        });
        setActive(false);
        setAddons(updatedAddons);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const toggleFormEdit = async (num: number) => {
    const sectionKeys: Record<number, string> = {
      1: "docs",
      2: "nutun",
      3: "sms",
      4: "basic",
    };
    const sectionKey = sectionKeys[num];

    if (
      !(await confirmDiscardFormEdits(formDisabled, formDirty, sectionKey))
    ) {
      return;
    }

    if (num === 1) {
      setFormDisabled({
        docs: !formDisabled.docs,
        nutun: formDisabled.nutun,
        sms: formDisabled.sms,
        basic: formDisabled.basic,
      });
    } else if (num === 2) {
      setFormDisabled({
        docs: formDisabled.docs,
        nutun: !formDisabled.nutun,
        sms: formDisabled.sms,
        basic: formDisabled.basic,
      });
    } else if (num === 3) {
      setFormDisabled({
        docs: formDisabled.docs,
        nutun: formDisabled.nutun,
        sms: !formDisabled.sms,
        basic: formDisabled.basic,
      });
    } else {
      setFormDisabled({
        docs: formDisabled.docs,
        nutun: formDisabled.nutun,
        sms: formDisabled.sms,
        basic: !formDisabled.basic,
      });
    }
  };

  const convertFileToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event?.target?.result;
        resolve(base64);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDeleteProduct = (id: string, key: string) => {
    setKey(key);
    setConfirmDelete(true);
    setProductToDelete(id);
  };

  const handleDeleteDocument = (id: string) => {
    setActive(true);

    removeDocumentApi(id)
      .then((res) => {
        const deleted = res.data;
        setDocuments(
          documents.filter((document: any) => document.id !== deleted.id)
        );
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const handleBack = () => {
    void navigate("/groups");
  };

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 14 },
  };

  const tailLayout = {
    labelCol: { span: 10 },
    wrapperCol: { offset: 6, span: 14 },
  };

  const setupIssues = getCommunicationSetupIssues({
    group,
    documents,
    communicationDocuments,
    formData,
  });

  const navigateToCommunicationSection = (section: string) => {
    setActiveGroupTab("communication");
    setCommunicationSubTab(section);
  };

  const items: TabsProps["items"] = [
    {
      key: "group-info",
      label: `Group Info`,
      children: (
        <div className="content-border">
          <CommunicationSetupAlert
            issues={setupIssues}
            onNavigate={navigateToCommunicationSection}
          />
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Basic Info"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Group Name:</h4>
                  <span> {group.GroupName}</span>
                </div>
                <div className="card-content">
                  <h4>Cellphone:</h4>
                  <span>{group.Cellphone}</span>
                </div>
                <div className="card-content">
                  <h4>Telephone:</h4>
                  <span> {group.TelNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Email:</h4>
                  <span> {group.Email}</span>
                </div>
                <div className="card-content">
                  <h4>Fax Number:</h4>
                  <span> {!group.FaxNumber ? "N/A" : group.FaxNumber}</span>
                </div>
                <div className="card-content">
                  <h4>FSP Number:</h4>
                  <span> {!group.FSPNumber ? "N/A" : group.FSPNumber}</span>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="Address"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Address Line 1:</h4>
                  <span> {group.AddressLine1}</span>
                </div>
                <div className="card-content">
                  <h4>Address Line 2:</h4>
                  <span> {group.AddressLine2}</span>
                </div>
                <div className="card-content">
                  <h4>Suburb:</h4>
                  <span> {group.Suburb}</span>
                </div>
                <div className="card-content">
                  <h4>City/Town:</h4>
                  <span> {group.CityTown}</span>
                </div>
                <div className="card-content">
                  <h4>Province:</h4>
                  <span> {group.Province}</span>
                </div>
                <div className="card-content">
                  <h4>Postal Code:</h4>
                  <span> {group.PostalCode}</span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "products",
      label: `Products`,
      children: (
        <div className="content-border">
          <ProductTable
            products={products}
            groupId={groupId}
            handleAdd={handleAddProduct}
            handleUpdate={handleUpdateProduct}
            handleDelete={handleDeleteProduct}
          />
        </div>
      ),
    },
    {
      key: "addons",
      label: `Addons`,
      children: (
        <div className="content-border">
          <AddonTable
            addons={addons}
            groupId={groupId}
            handleAdd={handleAddAddon}
            handleUpdate={handleUpdateAddon}
            handleDelete={handleDeleteProduct}
          />
        </div>
      ),
    },
    {
      key: "communication",
      label: "Communication",
      children: (
        <GroupCommunicationTab
          mode="tenant"
          groupId={groupId}
          group={group}
          documents={documents}
          communicationDocuments={communicationDocuments}
          form={form}
          formData={formData}
          formDisabled={formDisabled}
          loading={loading}
          layout={layout}
          tailLayout={tailLayout}
          documentOptions={document}
          textAreaRefs={textAreaRefs}
          communicationSubTab={communicationSubTab}
          setCommunicationSubTab={(key) => {
            setCommunicationSubTab(key);
            setGroupTabState(
              groupId,
              { activeGroupTab: "communication", communicationSubTab: key },
              false
            );
          }}
          setupIssues={setupIssues}
          onNavigateToSection={navigateToCommunicationSection}
          onDeleteDocument={handleDeleteDocument}
          onSmsConfigSubmit={(values) => handleAddDetails(groupId, values, 1)}
          onSmsCredentialsSubmit={(values) =>
            handleAddDetails(groupId, values, 3)
          }
          onEmailSettingsSubmit={(values) =>
            handleAddDetails(groupId, values, 4)
          }
          onSendBulkSms={(values) => handleSendCustomSms(groupId, values)}
          onToggleSmsConfigEdit={() => toggleFormEdit(1)}
          onToggleEmailSettingsEdit={() => toggleFormEdit(4)}
          onAddVariable={handleAddVariable}
          onSelectTextArea={setSelectedTextArea}
          onSmsEnabledChange={(checked) => onChange("SmsEnabled", checked)}
          onEmailNotificationChange={(checked) =>
            onChange("EmailNotification", checked)
          }
          onChangeDocument={onChangeDocument}
          onFormDirty={() => setFormDirty(true)}
          messageTypeOptions={messageType}
          emailTemplateOptions={emailTemplateOptions}
        />
      ),
    },
    ...(group.GroupLevel === 1
      ? [
          {
        key: "integration",
        label: `Integration Info`,
        children: (
          <div className="content-border">
            <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
              <Col span={12}>
                <Card
                  title="Debicheck Info"
                  style={{ backgroundColor: "	#DCDCDC" }}
                  bordered={true}
                  hoverable={true}
                >
                  {/* <div className="card-content">
                    <h4>Nutun Username:</h4>
                    <span> {group.NutunUsername}</span>
                  </div>
                  <div className="card-content">
                    <h4>Nutun Password:</h4>
                    <SecretText value={group.NutunPassword} />
                  </div>
                  <div className="card-content">
                    <h4>Nutun Usercode:</h4>
                    <span> {group.NutunUserCode}</span>
                  </div>
                  <div className="card-content">
                    <h4>Nutun Logocode:</h4>
                    <span> {group.NutunLogoCode}</span>
                  </div>
                  <div className="card-content">
                    <h4>Nutun Shortname:</h4>
                    <span> {group.NutunShortname}</span>
                  </div>
                  <div className="card-content">
                    <h4>Nutun Unique ID:</h4>
                    <span> {group.NutunUniqueID}</span>
                  </div>
                  <div className="card-content">
                    <h4> Nutun Debit Type:</h4>
                    <span> {group.NutunDebitValueType}</span>
                  </div> */}

                  {/* <Divider /> */}

                  <div className="card-content">
                    <h4>Nupay Username:</h4>
                    <span> {group.NupayUsername}</span>
                  </div>
                  <div className="card-content">
                    <h4>Nupay Password:</h4>
                    <SecretText value={group.NupayPassword} />
                  </div>
                  <div className="card-content">
                    <h4>Nupay Merchant Number:</h4>
                    <span> {group.NupayMerchantNumber}</span>
                  </div>
                  <div className="card-content">
                    <h4>Nupay Min Amount:</h4>
                    <span> {group.NupayMinAmount}</span>
                  </div>

                  <Divider />

                  <div className="card-content">
                    <h4>Pay@ Prefix:</h4>
                    <span> {group.PayAtPrefix}</span>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  title="SFTP Info"
                  style={{ backgroundColor: "	#DCDCDC" }}
                  bordered={true}
                  hoverable={true}
                >
                  <div className="card-content">
                    <h4>SFTP URL:</h4>
                    <span> {group.SftpURL}</span>
                  </div>
                  <div className="card-content">
                    <h4>SFTP Username:</h4>
                    <span> {group.SftpUsername}</span>
                  </div>
                  <div className="card-content">
                    <h4>SFTP Password:</h4>
                    <SecretText value={group.SftpPassword} />
                  </div>
                  <div className="card-content">
                    <h4>SFTP ID Directory:</h4>
                    <span> {group.SftpIDDirectory}</span>
                  </div>
                  <div className="card-content">
                    <h4>SFTP Application Directory:</h4>
                    <span> {group.SftpApplicationDirectory}</span>
                  </div>
                  <div className="card-content">
                    <h4>SFTP Policy Directory:</h4>
                    <span> {group.SftpPolicyDirectory}</span>
                  </div>
                  <div className="card-content">
                    <h4>SFTP Data Directory:</h4>
                    <span> {group.SftpDataDirectory}</span>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        ),
      },
        ]
      : []),
    {
      key: "basic-info",
      label: `Basic Info`,
      children: (
        <div className="content-border">
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Basic Info"
                bordered={true}
                hoverable={true}
                extra={
                  <EditOutlined
                    onClick={() => toggleFormEdit(4)}
                    style={{ color: "blue" }}
                  />
                }
              >
                <Form
                  {...layout}
                  onFinish={(values) => handleAddDetails(groupId, values, 4)}
                  initialValues={formData}
                  disabled={formDisabled.basic}
                  form={form}
                  onValuesChange={() => setFormDirty(true)}
                >
                  <div className="card-content">
                    <Form.Item name="AccountNumber" label="Account Number">
                      <Input size="large" placeholder="Account Number" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="AccountType" label="Account Type">
                      <Input size="large" placeholder="Account Type" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="Bank" label="Bank">
                      <Select
                        id="Bank"
                        options={bank}
                        size="large"
                        style={{ width: "100%" }}
                        placeholder="Bank"
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="BranchCode" label="Branch Code">
                      <Input size="large" placeholder="Branch Code" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="VATEnabled" label="VAT Enabled">
                      <Switch
                        defaultChecked={formData?.VATEnabled}
                        onChange={(checked) => onChange("VATEnabled", checked)}
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="CanRunOwnLapses"
                      label="Can run own lapses"
                      extra="When off, overdue members bubble up to the nearest parent that can run its own lapses."
                    >
                      <Switch
                        defaultChecked={formData?.CanRunOwnLapses}
                        onChange={(checked) =>
                          onChange("CanRunOwnLapses", checked)
                        }
                      />
                    </Form.Item>
                  </div>

                  {formData?.VATEnabled && (
                    <Form.Item
                      name="VATNumber"
                      label="VAT Number"
                      rules={[
                        {
                          required: formData?.VATEnabled,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="VAT Number" />
                    </Form.Item>
                  )}

                  <div className="card-content">
                    <Form.Item name="VariablePremium" label="Variable Premium">
                      <Switch
                        defaultChecked={formData?.VariablePremium}
                        onChange={(checked) =>
                          onChange("VariablePremium", checked)
                        }
                      />
                    </Form.Item>
                  </div>

                  <div className="card-content">
                    <Form.Item name="CompanyLogo" label="Company Logo">
                      <Upload
                        listType="picture"
                        defaultFileList={[
                          {
                            uid: "-1",
                            name: "Company_Logo",
                            status: "done",
                            url: formData?.CompanyLogo,
                            thumbUrl: formData?.CompanyLogo,
                          },
                        ]}
                        {...props}
                      >
                        <Button icon={<UploadOutlined />}>
                          Click to Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </div>

                  {formDisabled.basic === false && (
                    <div className="card-content">
                      <Form.Item {...tailLayout}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Button
                            style={{ marginBottom: 10 }}
                            block
                            size="large"
                            htmlType="submit"
                            loading={loading}
                          >
                            Submit
                          </Button>
                        </Space>
                      </Form.Item>
                    </div>
                  )}
                </Form>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  const onCancelDelete = () => {
    setConfirmDelete(false);
    setProductToDelete("");
  };

  const onConfirmDelete = () => {
    setConfirmDelete(false);
    setActive(true);

    removeProductApi(productToDelete)
      .then((res) => {
        const deleted = res.data;
        setProducts(
          products.filter((product: any) => product.id !== deleted.id)
        );
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  useEffect(() => {
    getGroupApi(String(groupId))
      .then((res) => {
        setCommunicationDocuments(res.data.CommunicationDocument);
        setDocuments(res.data.Document);
        setProducts(res.data.Product);
        setAddons(res.data.Addon);
        setFormData(res.data);
        setGroup(res.data);
        setActive(false);
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  }, [groupId, isActive]);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <GoldOutlined
                  style={{
                    paddingRight: "5px",
                    color: "#f04f05",
                  }}
                />
                {group.GroupName}
              </h2>
              <div className="interactions">
                <Button onClick={handleBack} size="large">
                  <ArrowLeftOutlined style={{ paddingRight: "5px" }} />
                  Back
                </Button>
              </div>
              <Tabs
                type="card"
                activeKey={activeGroupTab}
                onChange={(key) => {
                  setActiveGroupTab(key);
                  setGroupTabState(
                    groupId,
                    { activeGroupTab: key, communicationSubTab },
                    false
                  );
                }}
                items={items}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* Confirmation Modal */}
      {key === "1" ? (
        <Modal
          title="Confirm Remove"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Product inactive?
        </Modal>
      ) : (
        <Modal
          title="Confirm Revert"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Product active?
        </Modal>
      )}
    </LoadingOverlay>
  );
};

export default GroupManagementPage;
